import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LayerToolDefaults, MapLayerToolForm, LayerToolState, AbstractLayerTool } from 'geovisto';

// Geovisto core
/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class TilesLayerToolDefaults extends LayerToolDefaults {
    /**
     * It returns a unique type string of the tool which is based on the layer it wraps.
     */
    getType() {
        return TilesLayerToolDefaults.TYPE;
    }
    /**
     * It returns the layer name.
     */
    getLayerName() {
        return "Map layer";
    }
    /**
     * It returns the label of the tool.
     */
    getLabel() {
        return this.getLayerName();
    }
    /**
     * It returns the icon of the tool.
     */
    getIcon() {
        return '<i class="fa fa-globe"></i>';
    }
    /**
     * It returns the preferred base map.
     */
    getBaseMap() {
        return {
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            //url: 'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}';
            //url: 'https://mapserver.mapy.cz/turist-m/{z}-{x}-{y}';
            //url: 'http://mapserver.mapy.cz/base-m/{z}-{x}-{y}';
            maxZoom: 20,
            maxNativeZoom: 19
            //subdomains:['mt0','mt1','mt2','mt3']
        };
    }
}
/**
 * Static tool type constant.
 */
TilesLayerToolDefaults.TYPE = "geovisto-tool-layer-tiles";

// Geovisto core
/**
 * This class provides functions for management of the layer sidebar tab.
 *
 * @author Jiri Hynek
 */
class TilesLayerToolMapForm extends MapLayerToolForm {
    /**
     * It creates new map form with respect to the given props.
     *
     * @param tool
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It updates selected input values according to the given dimensions.
     *
     * @param dimensions
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setInputValues(dimensions) {
        return;
    }
    /**
     * It returns tab pane which will be placed in sidebar tab.
     */
    getContent() {
        // for now, it returns empty div element
        return document.createElement("div");
    }
}

// Geovisto core
/**
 * This class provide functions for using the state of the tiles layer tool.
 *
 * @author Jiri Hynek
 */
class TilesLayerToolState extends LayerToolState {
    /**
     * It creates a tool state.
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It resets state with respect to initial props.
     */
    initialize(defaults, props, initProps) {
        // the map layer tool properties
        this.setBaseMap(props.baseMap == undefined ? defaults.getBaseMap() : props.baseMap);
        // set super props
        super.initialize(defaults, props, initProps);
    }
    /**
     * The metod takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        super.deserialize(config);
        // the map layer tool config
        // TODO
    }
    /**
     * The method serializes the tool state. Optionally, defaults can be set if property is undefined.
     *
     * @param defaults
     */
    serialize(defaults) {
        const config = super.serialize(defaults);
        // serialize the map layer tool properties
        // TODO
        return config;
    }
    /**
     * It returns a base map ID.
     */
    getBaseMap() {
        return this.tilesModel;
    }
    /**
     * It sets a base map ID.
     *
     * @param baseMap
     */
    setBaseMap(baseMap) {
        this.tilesModel = baseMap;
    }
    /**
     * It returns a Leaflet tile layer.
     */
    getTileLayer() {
        return this.layer;
    }
    /**
     * It sets a Leaflet tile layer.
     *
     * @param layer
     */
    setTileLayer(layer) {
        this.layer = layer;
    }
}

// leaflet
/**
 * This class represents Map layer tool. It use tile layer and OSM maps.
 *
 * @author Jiri Hynek
 */
class TilesLayerTool extends AbstractLayerTool {
    /**
     * It creates a new tool with respect to the props.
     *
     * @param props
     */
    constructor(props) {
        super(props);
    }
    /**
     * It creates a copy of the uninitialized tool.
     */
    copy() {
        return new TilesLayerTool(this.getProps());
    }
    /**
     * Help function which returns the props given by the programmer.
     */
    getProps() {
        return super.getProps();
    }
    /**
     * It returns default values of the state properties.
     */
    getDefaults() {
        return super.getDefaults();
    }
    /**
     * It creates new defaults of the layer tool.
     */
    createDefaults() {
        return new TilesLayerToolDefaults();
    }
    /**
     * It returns the layer tool state.
     */
    getState() {
        return super.getState();
    }
    /**
     * It creates new defaults of the layer tool.
     */
    createState() {
        return new TilesLayerToolState(this);
    }
    /**
     * It returns a sidebar tab with respect to the configuration.
     */
    getMapForm() {
        if (this.mapForm == undefined) {
            this.mapForm = this.createMapForm();
        }
        return this.mapForm;
    }
    /**
     * It creates new tab control.
     */
    createMapForm() {
        // override if needed
        return new TilesLayerToolMapForm(this);
    }
    /**
     * Help function which acquires and returns the themes tool if available.
     */
    getThemesTool() {
        var _a;
        if (this.themesToolAPI == undefined) {
            const api = (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getToolsAPI();
            if (api.getGeovistoThemesTool) {
                this.themesToolAPI = api.getGeovistoThemesTool();
            }
        }
        return this.themesToolAPI;
    }
    /**
     * Overrides the super method.
     *
     * @param initProps
     */
    initialize(initProps) {
        return super.initialize(initProps);
    }
    /**
     * It creates layer items.
     */
    createLayerItems() {
        // create a tile layer
        const layer = this.createTileLayer(this.getState().getBaseMap());
        // update state
        this.getState().setTileLayer(layer);
        return [layer];
    }
    /**
     * Creates new tile layer
     *
     * @param tilesModel
     */
    createTileLayer(tilesModel) {
        var _a, _b, _c;
        const defaultTilesModel = this.getDefaults().getBaseMap();
        const options = {
            attribution: (_a = tilesModel.attribution) !== null && _a !== void 0 ? _a : "",
            maxZoom: (_b = tilesModel.maxZoom) !== null && _b !== void 0 ? _b : defaultTilesModel.maxZoom,
            maxNativeZoom: (_c = tilesModel.maxNativeZoom) !== null && _c !== void 0 ? _c : defaultTilesModel.maxNativeZoom
        };
        if (tilesModel.subdomains) {
            options.subdomains = tilesModel.subdomains;
        }
        const layer = L.tileLayer(tilesModel.url, options);
        return layer;
    }
    /**
     * This function is called when a custom event is invoked.
     *
     * @param event
     */
    handleEvent(event) {
        var _a;
        if (event.getType() == ((_a = this.getThemesTool()) === null || _a === void 0 ? void 0 : _a.getChangeEventType())) {
            this.onThemeChange(event.getChangedObject());
        }
    }
    /**
     * This function updates theme used in the tool.
     */
    onThemeChange(theme) {
        var _a;
        // update base map
        this.getState().setBaseMap(theme.getBaseMap());
        let layer = this.getState().getTileLayer();
        // TODO: use public API
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (layer && layer._url != theme.getBaseMap()) {
            // remove the old layer
            const leafltMap = (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getLeafletMap();
            if (leafltMap) {
                leafltMap.removeLayer(layer);
                // create a new tile layer
                layer = this.createTileLayer(this.getState().getBaseMap());
                // update state
                this.getState().setTileLayer(layer);
                // add the new layer to the leaflet map
                layer.addTo(leafltMap);
            }
        }
    }
}

const GeovistoTilesLayerTool = {
    getType: () => TilesLayerToolDefaults.TYPE,
    createTool: (props) => new TilesLayerTool(props),
};

export { GeovistoTilesLayerTool, TilesLayerTool, TilesLayerToolDefaults, TilesLayerToolMapForm, TilesLayerToolState };
//# sourceMappingURL=index.es.js.map
